import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { gridtoolsActions } from '../../redux/actions';
import { Container, Section, DrawOptions, VerticalSpace, EmptySpace, Button, ButtonGroup, Label, ButtonRow, TokenSelector, TextInput, TabGrou, Tab, TabContent} from './GridTools.styles';
import T, { Translate } from '../i18n/i18n';
import Checkbox from '../Forms/Checkbox/Checkbox';
import Operations from '../Table/Grid/Util/Operations';
import SceneryList from './SceneryList/SceneryList';
import ImagesList from './ImagesList/ImagesList';
import ColorSelector from '../ColorSelector/ColorSelector';
import LayerSelector from './LayerSelector/LayerSelector';
import Keyboard from '../../services/keyboardShortcuts';

export default ({tableId}) => {

    const dispatch = useDispatch();
    const [selectedTool, setSelectedTool] = useState('none');
    const [selectedSnap, setSelectedSnap] = useState('auto');
    const [propsColor, setPropsColor] = useState('rgba(255,255,255,1)');
    const [propsFinalColor, setPropsFinalColor] = useState('');
    const [propsCharId, setPropsCharId] = useState('');
    const [propImage, setPropImage] = useState('');
    const [isGMMode, setIsGMMode] = useState(false);
    const [activeTab, setActiveTab] = useState('draw');
    const [propTextValue, setPropTextValue] = useState('');
    
    const characterList = useSelector(state => state.charactersState.characters);
    const gridtoolsTool = useSelector(state => state.gridtoolsState.tool);
    const selectedShapes = useSelector(state => state.gridtoolsState.selectedShapes);
    const layers = useSelector(state => state.gridtoolsState.layers);
    const selectedLayer = useSelector(state => state.gridtoolsState.selectedLayer);
    const gridtoolsProps = useSelector(state => state.gridtoolsState.props);
    const gridtoolsSave = useSelector(state => state.gridtoolsState.shouldSaveProps);

    const numSelecteds = useRef(0);
    const currentTool = useRef('');

    useEffect(() => {
        numSelecteds.current = selectedShapes ? selectedShapes.length : 0;

        if (numSelecteds.current > 0) {
            let color = selectedShapes[0].props.color;
            let text = selectedShapes[0].props.text;
            let token = selectedShapes[0].props.token;
            let image = selectedShapes[0].props.image;

            if (color) {
                setPropsColor(color);
            }
            if (text) {
                setPropTextValue(text);
            }
            if (token) {
                setPropsCharId(token);
            }
            if (image) {
                setPropImage(image);
            }
        }
    }, [selectedShapes]);

    useEffect(() => {
        if (numSelecteds.current <= 1 && gridtoolsSave) {
            dispatch(gridtoolsActions.setProperties({
                color: propsFinalColor,
                token: propsCharId,
                text: propTextValue,
                image: propImage
            }));
        }
    }, [dispatch, gridtoolsSave, propsFinalColor, propsCharId, propTextValue, propImage]);

    useEffect(() => {
        if (numSelecteds.current <= 1 && propsColor) {
            dispatch(gridtoolsActions.setProperties({
                ...gridtoolsProps,
                color: propsColor,
            }, false));
        }
    }, [dispatch, propsColor]);

    useEffect(() => {
        dispatch(gridtoolsActions.setProperties({gmMode: isGMMode}));
    }, [dispatch, isGMMode]);

    useEffect(() => {
        setPropsCharId(characterList[0] ? characterList[0].id : null);
    }, [characterList]);

    useEffect(() => {
        currentTool.current = gridtoolsTool;
    }, [gridtoolsTool]);

    useEffect(() => {
        if (gridtoolsProps && gridtoolsSave) {
            handlePropagate();
        }
    }, [gridtoolsProps, gridtoolsSave]);
    
    useEffect(() => {
        if (propsFinalColor) {
            dispatch(gridtoolsActions.propagateChanges());
        }
    }, [propsFinalColor]);

    useEffect(() => {
        let delFn = () => {
            if (currentTool.current === Operations.SELECT && numSelecteds.current > 0) {
                dispatch(gridtoolsActions.setToolMode(Operations.REMOVE));
            }
            return true;
        };

        let f1Fn = () => {
            dispatch(gridtoolsActions.setSnapMode('auto'));
            setSelectedSnap('auto');
            return true;
        };

        let f2Fn = () => {
            dispatch(gridtoolsActions.setSnapMode('center'));
            setSelectedSnap('center');
            return true;
        };

        let f3Fn = () => {
            dispatch(gridtoolsActions.setSnapMode('corner'));
            setSelectedSnap('corner');
            return true;
        };

        let f4Fn = () => {
            dispatch(gridtoolsActions.setSnapMode('none'));
            setSelectedSnap('none');
            return true;
        };

        Keyboard.register({key:'DELETE'}, delFn);
        Keyboard.register({key:'F1'}, f1Fn);
        Keyboard.register({key:'F2'}, f2Fn);
        Keyboard.register({key:'F3'}, f3Fn);
        Keyboard.register({key:'F4'}, f4Fn);

        return () => {
            Keyboard.unregister({key:'DELETE'}, delFn);
            Keyboard.unregister({key:'F1'}, f1Fn);
            Keyboard.unregister({key:'F2'}, f2Fn);
            Keyboard.unregister({key:'F3'}, f3Fn);
            Keyboard.unregister({key:'F4'}, f4Fn);
        }
    }, [dispatch]);

    useEffect(() => {
        setSelectedTool(gridtoolsTool);

        if ([Operations.SELECT, Operations.BRING_TO_FRONT, Operations.SEND_TO_BACK].indexOf(gridtoolsTool) >= 0) {
            return;
        }

        setPropsColor('rgba(255,255,255,1)');
        setPropsFinalColor('');
        setPropTextValue('');
        setPropImage(null);

        if (gridtoolsTool === Operations.DRAW_TOKEN && characterList[0]) {
            setPropsCharId(characterList[0].id);
        } else {
            setPropsCharId('');
        }
    }, [gridtoolsTool, characterList]);


    
    const handleColor = useCallback((c) => {
        setPropsColor(`rgba(${c.r},${c.g},${c.b},${c.a/100})`);
    },[]);

    const handleTokenSelected = (c) => {
        setPropsCharId(c);
    }

    const onToolSelected = (v) => {
        dispatch(gridtoolsActions.setToolMode(v === selectedTool ? 'none' : v));
    };

    const onSnapModeSelected = (v) => {
        dispatch(gridtoolsActions.setSnapMode(v));
        setSelectedSnap(v);
    };

    const activateTab = (tab) => {
        setActiveTab(tab);
        if (tab !== 'draw') {
            onToolSelected('');
        }
    }

    const isShownColorSelector = () => {
        const isSingleSelect = selectedShapes.length === 1;
        return (isSingleSelect && (selectedShapes[0].isShape() || selectedShapes[0].isText())) ||
               selectedTool === Operations.DRAW_RECTANGLE ||
               selectedTool === Operations.DRAW_ELLIPSE ||
               selectedTool === Operations.DRAW_POLYGON ||
               selectedTool === Operations.DRAW_TEXT;
    }

    const useImages = () => {
        return (selectedShapes.length === 1 && selectedShapes[0].hasImage()) || selectedTool === Operations.DRAW_IMAGE;
    }

    const handleTextInput = (value) => {
        setPropTextValue(value);
        dispatch(gridtoolsActions.setProperties({text: value}));
    }

    const handleImageChange = (value) => {
        setPropImage(value);
        dispatch(gridtoolsActions.setProperties({image: value}));
    }

    const handlePropagate = useCallback((c) => {
        if (c) {
            setPropsFinalColor(`rgba(${c.r},${c.g},${c.b},${c.a/100})`);
        } else {
            dispatch(gridtoolsActions.propagateChanges());
        }
    }, [dispatch]);

    const handleLayersChange = (layers, selectedLayer) => {
        dispatch(gridtoolsActions.setLayers(layers));
        dispatch(gridtoolsActions.setSelectedLayer(selectedLayer));
    }

    return <Container>
        <Checkbox onClick={(value) => {setIsGMMode(value)}}>
            <T value="gridtools.shareChanges"/>
        </Checkbox>
        <TabGrou>
            <Tab className={activeTab === 'draw' ? 'selected' : ''} onClick={(e)=>{activateTab('draw')}}><T value="gridtools.tabdraw"/></Tab>
            <Tab className={activeTab === 'scenery' ? 'selected' : ''} onClick={(e)=>{activateTab('scenery')}}><T value="gridtools.tabscenery"/></Tab>
        </TabGrou>
        <TabContent className={activeTab === 'scenery' ? 'shown' : 'hidden'}>
            <SceneryList tableId={tableId} />
        </TabContent>
        <TabContent className={activeTab === 'draw' ? 'shown' : 'hidden'}>
            <Label><T value={`gridtools.${selectedShapes.length > 0 ? 'layersmove' : 'layers'}`}/></Label>
            <LayerSelector value={layers} selected={selectedLayer} onChange={handleLayersChange} />
            <DrawOptions>
                <ButtonGroup>
                    <Label><T value={"gridtools.labeldraw"} /></Label>
                    <ButtonRow>
                        <Button className={selectedTool===Operations.DRAW_TOKEN ? 'selected' : ''} onClick={() => onToolSelected(Operations.DRAW_TOKEN)} icon={"\\F034E"} title={Translate('gridtools.token')} />
                        <Button className={selectedTool===Operations.DRAW_RECTANGLE ? 'selected' : ''} onClick={() => onToolSelected(Operations.DRAW_RECTANGLE)} icon={"\\F0763"} title={Translate('gridtools.square')} />
                        <Button className={selectedTool===Operations.DRAW_ELLIPSE ? 'selected' : ''} onClick={() => onToolSelected(Operations.DRAW_ELLIPSE)} icon={"\\F0EA1"} title={Translate('gridtools.ellipse')} />
                        <Button className={selectedTool===Operations.DRAW_TEXT ? 'selected' : ''} onClick={() => onToolSelected(Operations.DRAW_TEXT)} icon={"\\F150F"} title={Translate('gridtools.text')} />
                        <Button className={selectedTool===Operations.DRAW_POLYGON ? 'selected' : ''} onClick={() => onToolSelected(Operations.DRAW_POLYGON)} icon={"\\F04D2"} title={Translate('gridtools.polygon')} />
                        <Button className={selectedTool===Operations.DRAW_IMAGE ? 'selected' : ''} onClick={() => onToolSelected(Operations.DRAW_IMAGE)} icon={"\\F02E9"} title={Translate('gridtools.image')} />
                        <Button className={selectedTool===Operations.DRAW_BRUSH ? 'selected' : ''} onClick={() => onToolSelected(Operations.DRAW_BRUSH)} icon={"\\F00E3"} title={Translate('gridtools.brush')} />
                    </ButtonRow>
                </ButtonGroup>
                <EmptySpace />
                <ButtonGroup>
                    <Label><T value={"gridtools.labelselection"} /></Label>
                    <ButtonRow>
                        <Button className={selectedTool===Operations.SELECT ? 'selected' : ''} onClick={() => onToolSelected(Operations.SELECT)} icon={"\\F01C0"} title={Translate('gridtools.select')} />
                        <Button className={selectedTool===Operations.CHANGE_LAYER ? 'selected' : ''} onClick={() => onToolSelected(Operations.CHANGE_LAYER)} icon={"\\F09FE"} title={Translate('gridtools.changelayer')} />
                        <Button className={selectedTool===Operations.REMOVE ? 'selected' : ''} onClick={() => onToolSelected(Operations.REMOVE)} icon={"\\F0A7A"} title={Translate('delete')} />
                        <Button className={selectedTool===Operations.BRING_TO_FRONT ? 'selected' : ''} onClick={() => onToolSelected(Operations.BRING_TO_FRONT)} icon={"\\F0795"} title={Translate('gridtools.bringtofront')} />
                        <Button className={selectedTool===Operations.SEND_TO_BACK ? 'selected' : ''} onClick={() => onToolSelected(Operations.SEND_TO_BACK)} icon={"\\F0792"} title={Translate('gridtools.sendtoback')} />
                    </ButtonRow>
                </ButtonGroup>
                <ButtonGroup>
                    <Label><T value={"gridtools.labelalign"} /></Label>
                    <ButtonRow>
                        <Button className={selectedSnap==='auto' ? 'selected' : ''} onClick={() => onSnapModeSelected('auto')} icon={"\\F00C7"} title={Translate('gridtools.snapAuto')} />
                        <Button className={selectedSnap==='center' ? 'selected' : ''} onClick={() => onSnapModeSelected('center')} icon={"\\F00CB"} title={Translate('gridtools.snapcenter')} />
                        <Button className={selectedSnap==='corner' ? 'selected' : ''} onClick={() => onSnapModeSelected('corner')}  icon={"\\F00CE"} title={Translate('gridtools.snapCorner')} />
                        <Button className={selectedSnap==='none' ? 'selected' : ''} onClick={() => onSnapModeSelected('none')} icon={"\\F00CD"} title={Translate('gridtools.snapFree')} />
                    </ButtonRow>
                </ButtonGroup>
            </DrawOptions>
            <VerticalSpace />
            <Section><T value="gridtools.properties"/></Section>
            { (selectedTool === Operations.DRAW_TEXT || (selectedShapes.length === 1 && selectedShapes[0].isText())) && <>
                <Label><T value={"gridtools.textlabel"} /></Label>
                <TextInput placeholder={Translate('gridtools.textplaceholder')} value={propTextValue} onChange={(e) => {handleTextInput(e.target.value)}} />
            </>}
            { isShownColorSelector() && <>
                <Label><T value={"gridtools.colorlabel"} /></Label>
                <ColorSelector value={propsColor} onChange={handleColor} onBlur={handlePropagate} />
            </>}
            { useImages() && <>
                <Label><T value={"gridtools.imagelabel"} /></Label>
                <ImagesList value={propImage} onChange={handleImageChange} tableId={tableId}/> 
            </>}
            { (selectedTool === Operations.DRAW_TOKEN || (selectedShapes.length === 1 && selectedShapes[0].isToken())) && <>
                <Label><T value={"gridtools.tokenlabel"} /></Label>
                <TokenSelector value={propsCharId || ''} onChange={(e) => {handleTokenSelected(e.target.value)}}>
                    {characterList && characterList.length && characterList.map((char, i) => {
                        return <option key={i} value={char.id}>{char.name}</option>
                    })}
                </TokenSelector>
            </>}
        </TabContent>
    </Container>;
};